import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import {
  amountFormatter,
  useCountdownV2,
  updateAuctionEventStatus_AuctionFloor,
  sendEventAnnouncement_AuctionFloor,
  getAuctionEventStatusById_AuctionFloor,
} from "services";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../shadcn/components/ui/dialog";
import { Button } from "../../../../shadcn/components/ui/button";
import { Textarea } from "../../../../shadcn/components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../shadcn/components/ui/card";
import { Badge } from "../../../../shadcn/components/ui/badge";
import CheckIcon from "../../../../icons/CheckIcon";
import TimerDisplay from "./TimerDisplay";
import BidHistoryTable from "./BidHistoryTable";
import BroadcastMessages from "./BroadcastMessages";
import EventDetailsPanel from "./EventDetailsPanel";
import BiddersInfoSection from "./BiddersInfoSection";
import BidHistoryModel from "../../../bidder/Tabs/auctionFloor/components/BidHistoryModel";

interface BankerEventDataV3Props {
  id: any;
  data: any;
  serverTime: number;
  latestBidAmount: any;
  startTime: any;
  endTime: any;
  status: any;
  setStatus: any;
  currentTime: any;
  latestBid: any;
  prevBid: any;
  correctBids: any;
  wrongBids: any;
  announcements: any;
  bidders?: any;
}

export default function BankerEventDataV3(props: BankerEventDataV3Props) {
  const {
    id,
    data,
    serverTime,
    endTime,
    status,
    setStatus,
    latestBid,
    latestBidAmount,
    correctBids,
    wrongBids,
    announcements,
    bidders,
  } = props;

  const [timeOffset, setTimeOffset] = useState(0);
  const [currentTime, setCurrentTime] = useState<any>(0);
  const [announcementSuccess, setAnnouncementSuccess] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [openModal, setOpenModal] = useState(false);
  const [showBidHistoryModel, setShowBidHistoryModel] =
    useState<boolean>(false);

  useEffect(() => {
    if (serverTime) {
      const serverDateTime =
        DateTime.fromMillis(serverTime).setZone("Asia/Kolkata");
      const localDateTime = DateTime.now().setZone("Asia/Kolkata");
      const offset = serverDateTime.toMillis() - localDateTime.toMillis();
      setTimeOffset(offset);
    }
  }, [serverTime]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(
        DateTime.now()
          .plus({ milliseconds: timeOffset })
          .setZone("Asia/Kolkata")
          .toFormat("DDD hh:mm:ss a"),
      );
    }, 1000);
    return () => clearInterval(intervalId);
  }, [timeOffset]);

  const [hours, minutes, seconds, isTimeEnded] = useCountdownV2(
    endTime,
    timeOffset,
  );

  const checkBidStatus = async (checkStatus = 0) => {
    console.log("isTimeEnded inside checkBidStatus", isTimeEnded, checkStatus);
    if (isTimeEnded || checkStatus) {
      try {
        setTimeout(async () => {
          const res: any = await getAuctionEventStatusById_AuctionFloor(id);
          if (res?.status && res?.status === "end") {
            console.log("status came as end", res?.status);
            setStatus(res?.status);
          }
        }, 1000);
      } catch (error) {
        console.log("error", error);
      }
    }
  };

  useEffect(() => {
    console.log("isTimeEnded::>", isTimeEnded);
    (async () => {
      await checkBidStatus(isTimeEnded);
    })();
  }, [isTimeEnded]);

  const handleCancelEvent = async (eventId: string) => {
    try {
      const cancelEventResponse = await updateAuctionEventStatus_AuctionFloor(
        eventId,
        "cancelled",
        cancellationReason.trim(),
      );
      setCancellationReason("");
      window.location.reload();
    } catch (err) {
      console.log("error", err);
    }
  };

  const handleAnnouncement = async (message: string) => {
    try {
      const payload = {
        event_uuid: id,
        announcement: message.trim(),
        type: "announcement",
      };
      const announcementResponse = await sendEventAnnouncement_AuctionFloor(
        payload,
      );

      if (announcementResponse) {
        setAnnouncementSuccess(true);
        // Reload announcements or update state as needed
        setTimeout(() => {
          setAnnouncementSuccess(false);
        }, 3000);
      }
    } catch (err) {
      console.log("error", err);
    }
  };

  const AnnouncementSuccessModal = () => {
    return (
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30">
        <div className="bg-white shadow-2xl rounded-xl w-full max-w-lg">
          <div className="w-full flex flex-col items-center px-4 py-6 border bg-[#FCFFFB] border-brand-bgGrey rounded-lg">
            <CheckIcon className="h-7 w-7 text-brand-bgSuccessGreen" />
            <div className="flex items-center space-x-3">
              <div>
                <p className="font-openSans text-center text-[22px] font-semibold text-brand-bgBlack">
                  Announcement sent to the bidders
                </p>
              </div>
            </div>

            <div className="w-full mt-8">
              <Button
                className="bg-brand-bgBlack text-white px-6 py-2 rounded-lg hover:bg-brand-bgBlack w-full"
                onClick={() => {
                  setAnnouncementSuccess(false);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getStatusText = () => {
    if (status === "end") return "Auction has ended";
    if (status === "cancelled") return "Auction has been cancelled";
    return "Live Auction";
  };

  const getBankName = () => {
    return data?.property_dump?.bank?.name || "N/A";
  };

  const getStateName = () => {
    return data?.property_dump?.state?.name || "N/A";
  };

  const getCityName = () => {
    return data?.property_dump?.city?.name || "N/A";
  };

  const formatBidTime = () => {
    if (status !== "end" && latestBid) {
      return currentTime;
    }
    if (latestBid?.frontend_timestamp) {
      return DateTime.fromMillis(
        typeof latestBid.frontend_timestamp === "string"
          ? parseInt(latestBid.frontend_timestamp)
          : latestBid.frontend_timestamp,
      )
        .setZone("Asia/Kolkata")
        .toFormat("DD hh:mm:ss a");
    }
    return currentTime;
  };

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 items-stretch">
        {/* Main Content Area */}
        <div className="lg:col-span-2 space-y-3 flex flex-col">
          {/* Top Card: Live Auction Header with Last Bidding and Timer */}
          <Card>
            <CardHeader className="p-4 pb-0">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="text-base font-semibold">
                    {getStatusText()}
                  </CardTitle>
                  <p className="text-xs text-gray-500">
                    {getBankName()} • Lot ID: {data?.listing_id || "N/A"}
                  </p>
                </div>
                <Badge
                  variant="outline"
                  className="bg-gray-50 border-gray-200 text-gray-600 text-xs px-2.5 py-0.5 flex items-center gap-1.5"
                >
                  {status === "live" && (
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                  )}
                  {status === "live" ? "Live • Banker View" : "Banker View"}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="flex justify-between items-end gap-4">
                <div className="flex-1">
                  <div className="text-[13px] text-gray-500 mb-0.5">
                    Last Bidding
                  </div>
                  <div className="text-[32px] font-bold text-orange-600 leading-none">
                    {latestBid ? `₹ ${amountFormatter(latestBidAmount)}` : "NA"}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    at {formatBidTime()} (IST)
                  </div>
                </div>
                <TimerDisplay
                  hours={hours}
                  minutes={minutes}
                  seconds={seconds}
                  status={status}
                  endTime={endTime}
                  extendTimeAt={data?.extend_time_at}
                />
              </div>
            </CardContent>
          </Card>

          {/* Bid History Section */}
          <BidHistoryTable
            correctBids={correctBids || []}
            wrongBids={wrongBids || []}
            status={status}
            setShowBidHistoryModel={setShowBidHistoryModel}
          />

          {/* Broadcast Messages Section */}
          <BroadcastMessages
            announcements={announcements || []}
            onSendMessage={handleAnnouncement}
            status={status}
          />

          {/* Bidders Information Section */}
          <BiddersInfoSection bidders={bidders} status={status} />
        </div>

        {/* Right Panel: Event Details */}
        <div className="lg:col-span-1 flex flex-col">
          <EventDetailsPanel
            data={data}
            latestBidAmount={latestBidAmount || data?.reserve_price}
            latestBid={latestBid}
            endTime={endTime}
            status={status}
            biddingType={data?.bidding_type}
            bank={getBankName()}
            state={getStateName()}
            city={getCityName()}
            setOpenModal={setOpenModal}
          />
        </div>
      </div>

      <BidHistoryModel
        app="banker"
        bidsEvent={correctBids}
        wrongBidsEvent={wrongBids}
        open={showBidHistoryModel}
        setOpen={setShowBidHistoryModel}
        isSwissAuction={false}
      />

      {/* Cancel Event Dialog */}
      <Dialog open={openModal} onOpenChange={setOpenModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to cancel this event? Please confirm your
              decision before proceeding
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center space-x-2 mt-4">
            <Textarea
              placeholder="Reason for cancelling the event"
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
            />
          </div>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-start">
            <Button
              className="bg-black text-white hover:bg-gray-600"
              variant="secondary"
              onClick={() => {
                if (id) handleCancelEvent(id.toString());
              }}
            >
              Send
            </Button>
            <DialogClose asChild>
              <Button
                className="mt-2 sm:mt-0"
                type="button"
                variant="secondary"
              >
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {announcementSuccess && <AnnouncementSuccessModal />}
    </>
  );
}
